import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { ToastrService } from 'ngx-toastr';
import { StatusEvent } from 'src/app/interfaces/Logs.interface';
import { IGenericRequestPayload, IRegisterRequestPayload, ISWFHMessagePayload } from 'src/app/interfaces/messages';
import { AppConfig } from 'src/app/services/app.config.services';
import { FaceLoginService } from 'src/app/services/face-login.service';
import { TrsEventsService } from 'src/app/services/trsevents.service';
import { wsService } from 'src/app/services/ws.service';
import { CloseCameraStream, GetCameraStreamInfo } from 'src/app/utils/camera';
import { getItemValueFromCookie } from 'src/app/utils/cookie';
import { LogUpload } from 'src/app/utils/log-upload';
import { log } from 'src/app/utils/logging';
import { isExistingLoginAvailable } from 'src/app/utils/login';
import { generateMessage } from 'src/app/utils/messages';
import { getUTCDateObj } from 'src/app/utils/timeManager';

const packageInfo = require('../../../../../../package.json');

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.sass']
})

export class LoginComponent implements OnInit {
  supervisorLanId = window.name ? this.trsService.supervisorId : localStorage.getItem("AgentLanId");
  connectionStatus: string = "text-warning";
  IsLoginDisabled = true;
  IsLoaded = false;
  IsReady = false;
  localVideoStream: MediaStream | null = null;

  welcomeToLabel: string = "";
  signInLabel: string = "";
  supervisorIdLabel: string = "";
  copyrightLabel: string = "";
  productNameLabel: string = "";
  lanidLabel: string = "";
  enterlanidLabel: string = "";

  @ViewChild('local_video') localVideo: ElementRef;
  appConfig = AppConfig;
  logUploader: LogUpload = new LogUpload("LoginComponent");
  packageInfo = packageInfo;

  constructor(private trsService: TrsEventsService, private toastService: ToastrService,
    private ws: wsService, private router: Router, private translationService: TranslocoService,
    private faceLoginService: FaceLoginService) {
  }

  ngOnInit(): void {
    this.logUploader.info("ngoninit", "Loading Login Page!");
    log.info("LoginComponent - ngOnInit");
    log.info(document.cookie, "identity: ", getItemValueFromCookie("identity"));

    if (this.appConfig.settings.SSO.Enabled === true) {
      const params = new URLSearchParams(document.location.search);
      var isConcurrent = params.get('IsConcurrent');

      this.IsLoaded = true;

      log.debug(`isConcurrent: ${isConcurrent}`);
      if (isConcurrent) {
        // this.toastService.warning("User already logged in");
        this.connectionStatus = "Concurrent";

        return;
      }
    }
    else {
      if (window.name && this.ws.getSupervisorId()) {
        if (this.appConfig.settings.defaults.FaceLoginEnabled === true)
          this.faceLoginService.isFaceAuthSuccess = true;
        this.handleLoginRequest(this.ws.getSupervisorId());
        return;
      }
      this.IsLoaded = true;
    }

    this.trsService.evtDataObservable.subscribe(response => {
      switch (response.eventName) {
        case "UPDATE_APP_LABELS":
          this.welcomeToLabel = this.translationService.translate("Welcome to");
          this.productNameLabel = this.translationService.translate("SWFH Supervisor");
          this.signInLabel = this.translationService.translate("Sign In");
          this.supervisorIdLabel = this.translationService.translate("Supervisor Id");
          this.copyrightLabel = this.translationService.translate("Copyright @Tetherfi");
          this.lanidLabel = this.translationService.translate("LAN ID");
          this.enterlanidLabel = this.translationService.translate("Please Enter Lan Id");
          break;
        case "ERROR":
          if (response.data != null && response.data.message != null)
            this.toastService.warning(response.data.message);
          break;
        case "WEBSOCKET_OPENED":
          this.connectionStatus = "text-success";
          this.logUploader.info("ngOnInit", "TRS WEBSOCKET_OPENED");
          break;
        case "WEBSOCKET_CLOSED":
          this.connectionStatus = "text-warning";
          this.logUploader.info("ngOnInit", "TRS WEBSOCKET_CLOSED");
          break;
        case "SESSIONTAKEOVERRESPONSE":
          this.disableLoginButton();
          break;
        case "PREREQUISITE_COMPLETE":
          this.IsReady = true;
          setTimeout(() => this.IsLoginDisabled = false, 1000);
          if (this.appConfig.settings.defaults.FaceLoginEnabled === true)
            this.getCameraStream();
          break;
        case "HIDE_HEADER":
          this.IsLoaded = true;
          break;
        case "PERFORM_SSO_LOGIN":
          this.SSOLogin();
          break;
        case "FACE_AUTH_LOGIN_SUCCESS":
          this.closeCameraStream();
          this.handleLoginRequest(this.supervisorLanId);
          break;
        case "FACE_AUTH_LOGIN_FAILURE":
          this.IsLoginDisabled = false;
          break;
      }
    });
  }

  disableLoginButton() {
    this.IsLoginDisabled = true;
    setTimeout(() => {
      this.IsLoginDisabled = false;
    }, 15000);
  }

  login = (): void => {
    this.trsService.UploadMilestoneAction({
      SummaryId: this.ws.traceId,
      Action: "Login Initiated",
      ActionDateTime: getUTCDateObj(),
      OtherData: "NA",
    });

    if (!this.supervisorLanId) {
      this.toastService.warning(this.enterlanidLabel);
      return;
    }
    if (this.IsLoginDisabled) {
      return;
    }

    const isAnExistingLoginAvailable: boolean = isExistingLoginAvailable(this.ws, this.toastService, this.trsService, this.translationService, false);

    if (isAnExistingLoginAvailable === true)
      return;

    if (this.isFaceAuthLoginEnabled() === true) {
      this.IsLoginDisabled = true;

      const canvas: HTMLCanvasElement = document.createElement('canvas');
      const context: CanvasRenderingContext2D | null = canvas.getContext('2d');

      if (context != null) {
        const videoElement: HTMLVideoElement = document.getElementById("___face-login-video-element") as HTMLVideoElement;

        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        const base64: string = canvas.toDataURL('image/jpeg');

        if (base64 == null || base64 == "") {
          this.toastService.warning(this.enterlanidLabel);
          this.IsLoginDisabled = false;
          return;
        }

        this.faceLoginService.setFaceAuthRequestInitialParams(this.supervisorLanId?.toUpperCase(), base64.split('base64,')[1]);

        log.debug(`REQUEST GETAGENTFEATURES`);
        const payload: IGenericRequestPayload = {
          EventName: "GETAGENTFEATURES",
          AgentLanId: this.supervisorLanId?.toUpperCase()
        };

        this.ws.sendMessage(JSON.stringify(generateMessage("TRSSESSION", "", payload, this.trsService)));
      }
    }
    else this.handleLoginRequest(this.supervisorLanId);
  }

  sendEvent(eventName: any, responseData: any) {
    let data = {
      "eventName": eventName,
      "data": responseData
    };
    this.trsService.evtdata.next(data);
  }

  SSOLogin = () => {
    try {
      log.debug(`SSO Login`);
      var sSSODomain = this.appConfig.settings.SSO.sSSODomain;
      const identity: string | null = getItemValueFromCookie("identity");
      this.logUploader.info("SSOLogin Auto Login", `Supervisor Id: ${identity}`);

      if (identity == null || identity === "") {
        var that = this;

        this.trsService.UploadMilestoneAction({
          SummaryId: this.ws.traceId,
          OtherData: "NA",
          Action: "SSO Login Initiated",
          ActionDateTime: getUTCDateObj(),
        });

        $.ajax({
          url: `${this.appConfig.settings.SSO.jwtAuthServerUrl}/Challenge?returnUrl=${window.location.href}?traceId=${this.ws.traceId},${window.location.href}?IsConcurrent=true&traceId=${this.ws.traceId}`,
          success: function (data: { cookieName: string; cookieValue: string; location: string; idpId: number; }, status: any, xhr: JQueryXHR) {
            that.IsLoaded = true;

            if (status === "nocontent" || data == null) {
              const now: Date = new Date();
              that.trsService.UploadMilestoneStatus({
                SummaryId: that.ws.traceId,
                Status: "Error",
                Reason: status,
                StatusDateTime: new Date(now.getTime() + now.getTimezoneOffset() * 60000)
              });
              log.error(`Unable to login via SSO`, status);
            }
            else {
              log.info(`SSO Login Success`, status, data);
              document.cookie = `${data.cookieName}=${data.cookieValue}; path=/; secure; samesite=none; domain=${sSSODomain}`;

              that.handleLoginRequest(that.supervisorLanId);

              if (that.appConfig.settings.SSO.Redirect.Enabled) {
                setTimeout(() => {
                  log.info(`Replacing Window Location: ${data.location}`);
                  window.location.replace(data.location);
                }, that.appConfig.settings.SSO.Redirect.Timer);
              }
              else {
                log.info(`Replacing Window Location: ${data.location}`);
                window.location.replace(data.location);
              }
            }
          },
          error: function (xhr: JQueryXHR, status: any, err: any) {
            const now: Date = new Date();
            let statusData: StatusEvent =
            {
              SummaryId: that.ws.traceId,
              Status: "Error",
              Reason: `Status Code: ${xhr.status},\n${xhr.responseText == null ? status : xhr.responseText}`,
              StatusDateTime: new Date(now.getTime() + now.getTimezoneOffset() * 60000)
            }
            that.trsService.UploadMilestoneStatus(statusData);
            log.error(`Exception in SSOLogin.Challenge.Call`, null, `Status Code: ${xhr.status},\n${xhr.responseText == null ? status : xhr.responseText}`);
          },
        });
      } else {
        this.IsLoaded = true;

        log.info(`SSOLogin: ${identity}`);

        this.handleLoginRequest(identity);
      }
    } catch (ex) {
      log.error(`Exception in SSOLogin.Call`, ex);
    }
  }

  handleLoginRequest = (agentLanId: string | null): void => {
    log.debug(`handleLoginRequest`, agentLanId);
    if (agentLanId == null || agentLanId === "") {
      this.router.navigate([""]);
      return;
    }
    this.trsService.supervisorId = agentLanId.toUpperCase();

    const payload: IRegisterRequestPayload = {
      Id: `SUPERVISOR_${agentLanId.toUpperCase()}`, // The LanID of the supervisor. SUPERVISOR_ prfix is required for TRS Server to undersand that its Supervisor login
      time: new Date(),
      Allowed: this.appConfig.settings.Allowed
    };
    const message: ISWFHMessagePayload = generateMessage("REGISTER", "", payload, this.trsService);

    log.debug(`REQUEST REGISTER`, message);
    this.ws.sendMessage(JSON.stringify(message));
  }

  getCameraStream = (): void => { this.localVideoStream = GetCameraStreamInfo(); }

  closeCameraStream = (): void => CloseCameraStream();

  isFaceAuthLoginEnabled = (): boolean => this.appConfig.settings.SSO.Enabled === false && this.appConfig.settings.IAM.Enabled === false && this.appConfig.settings.defaults.FaceLoginEnabled
}
